import type { ApiTransformationParams } from '@highimpact-frontend/api-types';
import {
  tr_aggregate,
  tr_aggregation_function,
  tr_categorisation,
  tr_condition,
  tr_function,
  tr_groupby,
  tr_not_equal,
} from '@highimpact-frontend/api-types';
import { buildPlotProjectParametersFromContextNames } from '@highimpact/react-hooks';

type MeasurePipelineFunctions =
  ApiTransformationParams['pipeline']['functions'];

/** Wind rose pipeline (direction + speed coupled sources). */
export function buildWindroseMeasurePipeline(): {
  readonly functions: MeasurePipelineFunctions;
} {
  return {
    functions: [
      tr_not_equal('1', null),
      tr_not_equal('0', null),
      tr_groupby('cardinal_direction', [
        tr_categorisation('N', [
          tr_condition('BetweenCondition', '0', { lower: 337.5, upper: 360 }),
        ]),
        tr_categorisation('N', [
          tr_condition('BetweenCondition', '0', { lower: 0, upper: 22.5 }),
        ]),
        tr_categorisation('NE', [
          tr_condition('BetweenCondition', '0', { lower: 22.5, upper: 67.5 }),
        ]),
        tr_categorisation('E', [
          tr_condition('BetweenCondition', '0', { lower: 67.5, upper: 112.5 }),
        ]),
        tr_categorisation('SE', [
          tr_condition('BetweenCondition', '0', { lower: 112.5, upper: 157.5 }),
        ]),
        tr_categorisation('S', [
          tr_condition('BetweenCondition', '0', { lower: 157.5, upper: 202.5 }),
        ]),
        tr_categorisation('SO', [
          tr_condition('BetweenCondition', '0', { lower: 202.5, upper: 247.5 }),
        ]),
        tr_categorisation('O', [
          tr_condition('BetweenCondition', '0', { lower: 247.5, upper: 292.5 }),
        ]),
        tr_categorisation('NO', [
          tr_condition('BetweenCondition', '0', { lower: 292.5, upper: 337.5 }),
        ]),
      ]),
      tr_function('binning', { column: '1', bins: 6 }),
      tr_aggregate(
        ['cardinal_direction', 'label_1'],
        [
          tr_aggregation_function('count', { column: 'horodate', prefix: true }),
          tr_aggregation_function('take_first', {
            column: 'cardinal_direction',
            default_value: 'unknown',
          }),
          tr_aggregation_function('take_first', {
            column: 'label_1',
            default_value: 'unknown',
          }),
        ]
      ),
      tr_function('project', {
        direction: 'cardinal_direction',
        speed: 'label_1',
        frequency: 'count_horodate',
      }),
      tr_function('sort', {
        criteria: [{ column: 'speed', order: 'ascending' }],
      }),
      tr_function('project', {
        'Direction du vent': 'direction',
        'Vitesse du vent': 'speed',
        Fréquence: 'frequency',
      }),
    ],
  };
}

/** Project params for bar/histogram: output keys reference post-sort column names. */
export function buildHistogramProjectParameters(
  contextIndexToName: Record<string, string>
): Record<string, string> {
  const projectParams: Record<string, string> = { horodate: 'horodate' };

  for (const [index, name] of Object.entries(contextIndexToName)) {
    // After sort, columns are named by context_index_to_name values. When those
    // values are trace labels, project is identity; when they are indices,
    // keep mapping output keys onto the indexed columns.
    projectParams[name] = /^\d+$/.test(name) ? index : name;
  }

  return projectParams;
}

/** Bar/histogram pipeline: sort then project columns to trace labels. */
export function buildHistogramMeasurePipeline(
  contextIndexToName: Record<string, string>
): { readonly functions: MeasurePipelineFunctions } {
  const projectParams = buildHistogramProjectParameters(contextIndexToName);

  return {
    functions: [
      tr_function('sort', {
        criteria: [{ column: 'horodate', order: 'ascending' }],
      }),
      tr_function('project', projectParams),
    ],
  };
}

/** Ombrothermic pipeline with decimation-aware project step. */
export function buildOmbrothermicMeasurePipeline(
  contextIndexToName: Record<string, string>,
  useDecimation: boolean
): { readonly functions: MeasurePipelineFunctions } {
  const projectParams = buildPlotProjectParametersFromContextNames(
    contextIndexToName,
    useDecimation
  );

  return {
    functions: [
      tr_function('sort', {
        criteria: [{ column: 'horodate', order: 'ascending' }],
      }),
      tr_function('project', projectParams),
    ],
  };
}

/** Default decimation for table-by-campaign widgets. */
export const OMBROTHERMIC_DEFAULT_DECIMATION = {
  type: 'by_point' as const,
  points: 2000,
};
